import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RequestService from '../../services/requestService';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Button,
  Alert
} from '@mui/material';
import { 
  PendingActions as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import MainLayout from '../../layout/MainLayout';

// Initialisierung der Daten
const initialStats = {
  totalRequests: 0,
  pendingRequests: 0,
  completedRequests: 0,
  revenue: 0
};

const initialChartData = [
  { name: "Jan", anfragen: 0, abgeschlossen: 0 },
  { name: "Feb", anfragen: 0, abgeschlossen: 0 },
  { name: "Mär", anfragen: 0, abgeschlossen: 0 }
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [recentRequests, setRecentRequests] = useState([]);
  const [stats, setStats] = useState(initialStats);
  const [chartData, setChartData] = useState(initialChartData);
  const [loading, setLoading] = useState(true);

  // Laden der Daten vom Backend
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        // Laden der Provider Dashboard Daten
        const providerStatsData = await RequestService.getStats(true);

        // Provider Statistics API gibt alles in einem Objekt zurück
        if (providerStatsData) {
          // Recent requests aus den provider stats extrahieren
          const recentRequestsData = providerStatsData.recent_activity || [];
          setRecentRequests(recentRequestsData);

          // Stats formatieren für Dashboard
          const formattedStats = {
            totalRequests: providerStatsData.total_orders || 0,
            pendingRequests: providerStatsData.pending_review || 0,
            completedRequests: providerStatsData.completed_orders || 0,
            revenue: providerStatsData.total_revenue || 0
          };
          setStats(formattedStats);

          // Chart data aus provider stats extrahieren
          const chartData = providerStatsData.monthly_chart_data || [];
          setChartData(chartData);
        }

        setLoading(false);
      } catch (error) {
        console.error("Fehler beim Laden der Dashboard-Daten:", error);
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);
  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <PendingIcon color="primary" />;
      case 'feasibility':
        return <PendingIcon color="warning" />;
      case 'negotiation':
        return <PendingIcon color="warning" />;
      case 'in_progress':
        return <PendingIcon color="warning" />;
      case 'completed':
        return <CheckCircleIcon color="info" />;
      case 'delivered':
        return <CheckCircleIcon color="success" />;
      case 'rejected':
        return <CancelIcon color="error" />;
      // Legacy status support
      case 'approved':
        return <CheckCircleIcon color="success" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      default:
        return <AssignmentIcon />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'new':
        return 'Neu';
      case 'feasibility':
        return 'Machbarkeitsprüfung';
      case 'negotiation':
        return 'Verhandlung';
      case 'in_progress':
        return 'In Bearbeitung';
      case 'completed':
        return 'Abgeschlossen';
      case 'delivered':
        return 'Übermittelt';
      case 'rejected':
        return 'Abgelehnt';
      // Legacy status support
      case 'approved':
        return 'Genehmigt';
      case 'pending':
        return 'In Bearbeitung';
      default:
        return status;
    }
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom component="div" sx={{ fontWeight: 'bold', mb: 4 }}>
          Dashboard
        </Typography>

        {/* Statistik-Karten */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={2} sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140, borderRadius: 2 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Anfragen gesamt
              </Typography>
              <Typography component="p" variant="h4" sx={{ flexGrow: 1 }}>
                {stats.totalRequests}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Alle Anfragen
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={2} sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140, borderRadius: 2 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Ausstehend
              </Typography>
              <Typography component="p" variant="h4" sx={{ flexGrow: 1 }}>
                {stats.pendingRequests}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PendingIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Zu bearbeitende Anfragen
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={2} sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140, borderRadius: 2 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Abgeschlossen
              </Typography>
              <Typography component="p" variant="h4" sx={{ flexGrow: 1 }}>
                {stats.completedRequests}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Erfolgreich bearbeitet
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={2} sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140, borderRadius: 2 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Umsatz
              </Typography>
              <Typography component="p" variant="h4" sx={{ flexGrow: 1 }}>
                {stats.revenue} €
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MoneyIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Generierter Umsatz
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Diagramm und Anfragenliste */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Anfragen-Übersicht
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="anfragen" name="Eingegangene Anfragen" fill="#8884d8" />
                  <Bar dataKey="abgeschlossen" name="Abgeschlossene Anfragen" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Neueste Anfragen
              </Typography>
              <List>
                {recentRequests.map((request, index) => (
                  <React.Fragment key={request.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemIcon>
                        {getStatusIcon(request.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={request.title}
                        secondary={
                          <React.Fragment>
                            <Typography
                              sx={{ display: 'inline' }}
                              component="span"
                              variant="body2"
                              color="text.primary"
                            >
                              {request.company}
                            </Typography>
                            {` — ${request.date}`}
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    <ListItem sx={{ pt: 0 }}>
                      <ListItemText
                        primary={
                          <Chip 
                            size="small" 
                            label={getStatusText(request.status)} 
                            color={
                              request.status === 'delivered' ? 'success' :
                              request.status === 'completed' ? 'info' :
                              request.status === 'rejected' ? 'error' :
                              request.status === 'new' ? 'primary' : 'warning'
                            }
                          />
                        }
                      />
                    </ListItem>
                    {index < recentRequests.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button variant="outlined" href="/incoming-requests">
                  Alle Anfragen anzeigen
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default Dashboard;
